import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks';
import { Dialog, DialogTrigger, DialogContent } from '../Dialog';

// Definición de la variante para la forma de la imagen
const imageShapeVariants = cva('', {
	variants: {
		shape: {
			round: 'rounded-full',
			square: 'rounded',
			rectangular: 'rounded-none',
		},
	},
	defaultVariants: {
		shape: 'round',
	},
});

// Definición de las props del componente
export interface ZoomableImageProps
	extends React.ImgHTMLAttributes<HTMLImageElement>,
		VariantProps<typeof imageShapeVariants> {
	src: string;
	alt: string;
	className?: string;
	badgeSrc?: string;
	badgeAlt?: string;
	size?: 'sm' | 'md' | 'lg' | 'xl';
}

export const ZoomableImage = React.forwardRef<HTMLDivElement, ZoomableImageProps>(
	({ src, alt, className, badgeSrc, badgeAlt, size = 'md', shape, ...props }, ref) => {
		const isMobile = useIsMobile();
		if (!src) return null;

		// Calcular las dimensiones para la imagen principal en función de la forma y el tamaño
		let mainDimensions = '';
		if (shape === 'rectangular') {
			// Para forma rectangular se usa ancho completo y altura variable
			switch (size) {
				case 'sm':
					mainDimensions = isMobile ? 'w-full h-28' : 'w-full h-20';
					break;
				case 'md':
					mainDimensions = isMobile ? 'w-full h-56' : 'w-full h-40';
					break;
				case 'lg':
					mainDimensions = isMobile ? 'w-full h-80' : 'w-full h-60';
					break;
				case 'xl':
					mainDimensions = isMobile ? 'w-full h-96' : 'w-full h-80';
					break;
				default:
					mainDimensions = isMobile ? 'w-full h-56' : 'w-full h-40';
			}
		} else {
			// Para formas round o square
			switch (size) {
				case 'sm':
					mainDimensions = isMobile ? 'w-32 h-32' : 'w-16 h-16';
					break;
				case 'md':
					mainDimensions = isMobile ? 'w-64 h-64' : 'w-32 h-32';
					break;
				case 'lg':
					mainDimensions = isMobile ? 'w-80 h-80' : 'w-40 h-40';
					break;
				case 'xl':
					mainDimensions = isMobile ? 'w-96 h-96' : 'w-48 h-48';
					break;
				default:
					mainDimensions = isMobile ? 'w-64 h-64' : 'w-32 h-32';
			}
		}

		// Calcular dimensiones para el badge (imagen secundaria)
		let badgeDimensions = '';
		switch (size) {
			case 'sm':
				badgeDimensions = isMobile ? 'w-14 h-14' : 'w-8 h-8';
				break;
			case 'md':
				badgeDimensions = isMobile ? 'w-28 h-28' : 'w-12 h-12';
				break;
			case 'lg':
				badgeDimensions = isMobile ? 'w-32 h-32' : 'w-16 h-16';
				break;
			case 'xl':
				badgeDimensions = isMobile ? 'w-36 h-36' : 'w-20 h-20';
				break;
			default:
				badgeDimensions = isMobile ? 'w-28 h-28' : 'w-12 h-12';
		}

		// Combinar las clases de forma (a través de cva) con las dimensiones calculadas y clases base
		const mainImageClassName = cn(
			imageShapeVariants({ shape }),
			mainDimensions,
			'hover:shadow-2xl cursor-pointer hover:scale-105 transition-all duration-300 object-cover',
		);

		const badgeImageClassName = cn(
			imageShapeVariants({ shape }),
			badgeDimensions,
			'absolute bottom-0 right-0 border-2 border-white shadow-md cursor-pointer hover:scale-105 transition-all duration-300',
		);

		return (
			<div className={cn('relative', className)} ref={ref}>
				{/* Modal para la imagen principal */}
				<Dialog>
					<DialogTrigger asChild>
						<img src={src} alt={alt} className={mainImageClassName} {...props} />
					</DialogTrigger>
					<DialogContent className="max-w-7xl border-none p-0 bg-transparent">
						<div className="relative h-[calc(100vh-220px)] w-full overflow-clip rounded-md">
							<img src={src} alt={alt} className="h-full w-full object-contain" />
						</div>
					</DialogContent>
				</Dialog>

				{/* Modal para la imagen secundaria (badge) */}
				{badgeSrc && (
					<Dialog>
						<DialogTrigger asChild>
							<img src={badgeSrc} alt={badgeAlt} className={badgeImageClassName} />
						</DialogTrigger>
						<DialogContent className="max-w-7xl border-none p-0 bg-transparent">
							<div className="relative h-[calc(100vh-220px)] w-full overflow-clip rounded-md">
								<img
									src={badgeSrc}
									alt={badgeAlt}
									className="h-full w-full object-contain"
								/>
							</div>
						</DialogContent>
					</Dialog>
				)}
			</div>
		);
	},
);

ZoomableImage.displayName = 'ZoomableImage';
